import React, { useState, useEffect, useRef } from 'react';
import { useInternals } from '../../context/internalContext';
import Modal from '../../components/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faX } from '@fortawesome/free-solid-svg-icons';
import Webcam from 'react-webcam';

function AddImage({ internalId, isOpen, closeModal, onSaveSuccess }) {
  const { uploadInternalImage, listTypeImage, listDetailType } = useInternals();

  const [fileInputs, setFileInputs] = useState([{ file: null, type: '', detail_type: '', description: '' }]);
  const [imageTypes, setImageTypes] = useState([]);
  const [detailTypes, setDetailTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); //Botao enviando

  // Estado para controlar se está usando a câmera para cada input
  const [useCamera, setUseCamera] = useState(fileInputs.map(() => false));
  // Referência do componente Webcam para cada input
  const webcamRefs = useRef([]);
  // Estado para armazenar a imagem capturada para preview para cada input
  const [capturedImages, setCapturedImages] = useState(fileInputs.map(() => null));
  // Estado para validar se cada item possui imagem antes de enviar
  const [fileErrors, setFileErrors] = useState(fileInputs.map(() => false));

  useEffect(() => {
    const fetchTypes = async () => {
      const imageTypesData = await listTypeImage();
      setImageTypes(imageTypesData.results);
      const detailTypesData = await listDetailType();
      setDetailTypes(detailTypesData.results);
      console.log(detailTypesData.results)
    };

    fetchTypes();
  }, [listTypeImage, listDetailType]);

  // Atualiza os estados de useCamera e capturedImages quando fileInputs muda (ex: adiciona ou remove)
  useEffect(() => {
    setUseCamera(prev => {
      const newState = [...prev];
      while (newState.length < fileInputs.length) newState.push(false);
      while (newState.length > fileInputs.length) newState.pop();
      return newState;
    });
    setCapturedImages(prev => {
      const newState = [...prev];
      while (newState.length < fileInputs.length) newState.push(null);
      while (newState.length > fileInputs.length) newState.pop();
      return newState;
    });
    setFileErrors(prev => {
      const newState = [...prev];
      while (newState.length < fileInputs.length) newState.push(false);
      while (newState.length > fileInputs.length) newState.pop();
      return newState;
    });
  }, [fileInputs]);

  // Função para tentar ativar a câmera ao clicar no input visual
  const handleVisualInputClick = async (index) => {
    // Tenta ativar a câmera
    try {
      // Testar permissão para câmera
      await navigator.mediaDevices.getUserMedia({ video: true });
      // Se sucesso, habilita o modo câmera para o input
      const newUseCamera = [...useCamera];
      newUseCamera[index] = true;
      setUseCamera(newUseCamera);
    } catch (error) {
      // Se falhar (sem permissão ou sem câmera), abre seletor de arquivos tradicional
      // Criar input file temporário e disparar clique
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/jpeg,image/png,.jpg,.jpeg,.png';
      fileInput.onchange = (event) => handleFileImageChange(event, index);
      fileInput.click();
    }
  };

  // Captura a imagem da webcam e converte para File, armazenando no fileInputs
  const captureImage = (index) => {
    if (webcamRefs.current[index]) {
      const imageSrc = webcamRefs.current[index].getScreenshot();
      if (imageSrc) {
        // Converte dataURL para Blob
        const byteString = atob(imageSrc.split(',')[1]);
        const mimeString = imageSrc.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        // Cria um File a partir do Blob
        const file = new File([blob], `captured_image_${index}.png`, { type: mimeString });

        // Atualiza o fileInputs com o arquivo capturado
        const newFileInputs = [...fileInputs];
        newFileInputs[index].file = file;
        // Limpa erro deste item ao capturar imagem
        setFileErrors(prev => {
          const newState = [...prev];
          newState[index] = false;
          return newState;
        });
        setFileInputs(newFileInputs);

        // Atualiza o preview da imagem capturada
        const newCapturedImages = [...capturedImages];
        newCapturedImages[index] = imageSrc;
        setCapturedImages(newCapturedImages);

        // Sai do modo câmera
        const newUseCamera = [...useCamera];
        newUseCamera[index] = false;
        setUseCamera(newUseCamera);
      }
    }
  };

  // Cancela o modo câmera para o input
  const cancelCamera = (index) => {
    const newUseCamera = [...useCamera];
    newUseCamera[index] = false;
    setUseCamera(newUseCamera);
  };

  const handleFileImageChange = (event, index) => {
    const files = [...fileInputs];
    if (event.target.files && event.target.files[0]) {
      files[index].file = event.target.files[0];
      // Limpa erro deste item ao selecionar arquivo
      setFileErrors(prev => {
        const newState = [...prev];
        newState[index] = false;
        return newState;
      });
      setFileInputs(files);

      // Limpa preview de webcam caso tenha sido usado antes
      const newCapturedImages = [...capturedImages];
      newCapturedImages[index] = null;
      setCapturedImages(newCapturedImages);

      // Sai do modo câmera caso estivesse ativo
      const newUseCamera = [...useCamera];
      newUseCamera[index] = false;
      setUseCamera(newUseCamera);
    }
  };

  const handleInputChange = (event, index, key) => {
    const newInputs = [...fileInputs];
    newInputs[index][key] = event.target.value;
    setFileInputs(newInputs);
  };

  const addFileInput = () => {
    setFileInputs(prevState => [...prevState, { file: null, type: '', detail_type: '', description: '' }]);
  };

  const removeLastFileInput = () => {
    setFileInputs(prevState => prevState.slice(0, -1));
  };

  const handleImageSubmit = async (event) => {
    event.preventDefault();
    // Validação: não permitir salvar sem imagem em nenhum item
    const missingFileIndexes = fileInputs
      .map((input, idx) => (!input.file ? idx : null))
      .filter((idx) => idx !== null);

    if (missingFileIndexes.length > 0) {
      // Marca os itens sem arquivo para exibir mensagem no formulário
      setFileErrors(fileInputs.map((input) => !input.file));
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(true);

    const formDataArray = fileInputs.map(input => {
      const formData = new FormData();
      // Neste ponto a validação já garantiu que input.file existe
      formData.append('picture', input.file);
      formData.append('type', input.type);  // Usando ID do tipo de imagem
      formData.append('detail_type', input.detail_type); // Usando ID do tipo de detalhe
      formData.append('internal', internalId);
      formData.append('description', input.description);
      return formData;
    });

    try {
      const uploadResults = await Promise.all(
        formDataArray.map(formData => uploadInternalImage(formData))
      );

      if (uploadResults) {
        onSaveSuccess();
        closeModal();
      }
    } catch (error) {
      console.error('Erro ao salvar imagem', error);
    }
    finally{
      setIsSubmitting(false);
    }
  };

  return (
    <Modal title="Adicionar Imagem" isOpen={isOpen} closeModal={closeModal}>
      <div className='div-modal'>
        <form onSubmit={handleImageSubmit}>
          {fileInputs.map((input, index) => (
            <div key={index} className='form-group modal-form'>
              <label>Escolha a imagem</label>
              {!useCamera[index] ? (
                <>
                  {/* Input visual clicável que ativa a webcam ou seletor de arquivos
                      Estilo agora vem do internos.css */}
                  <div
                    className="input-visual"
                    onClick={() => handleVisualInputClick(index)}
                    aria-label="Clique para abrir câmera ou selecionar arquivo"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleVisualInputClick(index); }}
                  >
                    {input.file ? (
                      // Mostra nome do arquivo se já selecionado
                      <span>{input.file.name}</span>
                    ) : (
                      <span>Clique para abrir câmera ou selecionar arquivo</span>
                    )}
                  </div>
                  {fileErrors[index] && (
                    <p className="input-error-text">Selecione ou capture uma imagem antes de salvar.</p>
                  )}
                  {/* Input file escondido para fallback */}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                    onChange={(event) => handleFileImageChange(event, index)}
                    style={{ display: 'none' }}
                    id={`hidden-file-input-${index}`}
                  />
                  {/* Preview da imagem capturada ou selecionada
                      Estilo agora vem do internos.css */}
                  {input.file && !capturedImages[index] && (
                    <div className="image-preview">
                      <img
                        src={URL.createObjectURL(input.file)}
                        alt="Preview"
                        className="image-preview"
                      />
                    </div>
                  )}
                  {/* Preview da imagem capturada via webcam
                      Estilo agora vem do internos.css */}
                  {capturedImages[index] && (
                    <div className="image-preview">
                      <img
                        src={capturedImages[index]}
                        alt="Preview capturada"
                        className="image-preview"
                      />
                    </div>
                  )}
                </>
              ) : (
                // Modo câmera ativo
                <div className="image-upload-webcam">
                  {/* Componente Webcam
                      Estilo agora vem do internos.css */}
                  <Webcam
                    audio={false}
                    ref={el => webcamRefs.current[index] = el}
                    screenshotFormat="image/png"
                    videoConstraints={{ facingMode: { ideal: "environment" } }}
                  />
                  {/* Container dos botões da webcam
                      Estilo agora vem do internos.css */}
                  <div className="image-upload-actions">
                    <button
                      type="button"
                      onClick={() => captureImage(index)}
                      className="btn-primary"
                    >
                      Capturar
                    </button>
                    <button
                      type="button"
                      onClick={() => cancelCamera(index)}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        cancelCamera(index);
                        // Abre seletor de arquivos tradicional
                        const fileInput = document.createElement('input');
                        fileInput.type = 'file';
                        fileInput.accept = 'image/jpeg,image/png,.jpg,.jpeg,.png';
                        fileInput.onchange = (event) => handleFileImageChange(event, index);
                        fileInput.click();
                      }}
                      className="btn-secondary"
                    >
                      Escolher arquivo
                    </button>
                  </div>
                </div>
              )}
              <br /><br />
              <label>Tipo de imagem</label>
              <select
                value={input.type}
                onChange={(event) => handleInputChange(event, index, 'type')}
                required
              >
                <option value="">Selecione o tipo de imagem</option>
                {imageTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.type}</option> // Enviando o ID
                ))}
              </select>
              <br /><br />
              <label>Detalhe da imagem</label>
              <select
                value={input.detail_type}
                onChange={(event) => handleInputChange(event, index, 'detail_type')}
              >
                <option value="">Selecione o tipo de detalhe</option>
                {detailTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.detail}</option> // Enviando o ID e mostrando o detalhe correto
                ))}
              </select>
              <br /><br />
              <label>Descrição</label>
              <input
                type="text"
                placeholder="Descrição"
                value={input.description}
                onChange={(event) => handleInputChange(event, index, 'description')}
              />
            </div>

          ))}
          <div className="button-group">
            <button className='add-image' type="button" onClick={addFileInput}>
              <FontAwesomeIcon icon={faPlus} /> Mais Fotos
            </button>
            <br></br>
            {fileInputs.length > 1 && (
              <button className='remove-image' type="button" onClick={removeLastFileInput}>
                <FontAwesomeIcon icon={faX} />
              </button>
            )}
          </div>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Enviando...' : 'Salvar'}
          </button>
        </form>
      </div>
    </Modal>
  );
}

export default AddImage;
